/**
 * Q16.16 定点数运算。
 *
 * 模拟层的所有游戏量都必须用本模块运算。浮点数在不同 CPU / 浏览器 / JIT 状态下
 * 可能产生位级差异，累积后导致联机对战分叉（Desync）。
 *
 * 取值范围 ±32767.99998，精度 1/65536 ≈ 0.0000153。
 * 地图坐标以「格」为单位，最大 256×256 格，完全在范围内。
 */

/** 定点数。本质是 int32，约定低 16 位为小数部分。 */
export type Fix = number;

export const FRAC_BITS = 16;
export const ONE: Fix = 1 << FRAC_BITS; // 65536
export const HALF: Fix = ONE >> 1;
export const ZERO: Fix = 0;

/** 整数 → 定点数 */
export const fromInt = (n: number): Fix => (n << FRAC_BITS) | 0;

/**
 * 浮点 → 定点数。
 * 仅允许在「数据表加载」与「测试」中使用，禁止出现在模拟热路径中：
 * 数据表加载只发生一次且输入是常量，因此不破坏确定性。
 */
export const fromFloat = (n: number): Fix => Math.round(n * ONE) | 0;

/** 定点数 → 浮点。仅渲染层可用。 */
export const toFloat = (f: Fix): number => f / ONE;

/** 定点数 → 整数（向下取整） */
export const toInt = (f: Fix): number => f >> FRAC_BITS;

/** 向零取整 */
export const trunc = (f: Fix): Fix => (f >= 0 ? f & ~(ONE - 1) : -((-f) & ~(ONE - 1))) | 0;

/**
 * 定点数乘法。
 *
 * 把 a、b 各拆成高 16 位（有符号）与低 16 位（无符号）后分项相乘，
 * 避免 a*b 超出 double 的精确整数范围。
 */
export function mul(a: Fix, b: Fix): Fix {
  const ah = a >> FRAC_BITS;
  const al = a & 0xffff;
  const bh = b >> FRAC_BITS;
  const bl = b & 0xffff;
  return (
    ((Math.imul(ah, bh) << FRAC_BITS) +
      Math.imul(ah, bl) +
      Math.imul(al, bh) +
      ((Math.imul(al, bl) >>> FRAC_BITS) | 0)) |
    0
  );
}

/**
 * 定点数除法。
 *
 * a * ONE 的绝对值上限约 2^31 * 2^16 = 2^47，仍在 double 的精确整数范围（2^53）内，
 * 因此中间结果是精确的；IEEE 754 除法是「正确舍入」且跨平台确定的，随后截断为整数。
 */
export function div(a: Fix, b: Fix): Fix {
  if (b === 0) return a >= 0 ? 0x7fffffff : -0x7fffffff;
  return ((a * ONE) / b) | 0;
}

/** 定点数 × 整数（比 mul 快，用于热路径） */
export const mulInt = (a: Fix, n: number): Fix => Math.imul(a, n) | 0;

/** 定点数 ÷ 整数 */
export const divInt = (a: Fix, n: number): Fix => (n === 0 ? 0 : (a / n) | 0);

/** 按千分比缩放：scale(x, 1300) = x * 1.3。用于百分比修正，避免额外的定点常量。 */
export const permille = (a: Fix, per: number): Fix => ((a * per) / 1000) | 0;

export const abs = (a: Fix): Fix => (a < 0 ? -a : a);
export const min = (a: Fix, b: Fix): Fix => (a < b ? a : b);
export const max = (a: Fix, b: Fix): Fix => (a > b ? a : b);
export const clamp = (v: Fix, lo: Fix, hi: Fix): Fix => (v < lo ? lo : v > hi ? hi : v);
export const sign = (a: Fix): number => (a > 0 ? 1 : a < 0 ? -1 : 0);

/**
 * 定点数平方根，逐位二分法。
 *
 * 不使用 Math.sqrt：虽然 IEEE 754 要求 sqrt 正确舍入，但逐位整数算法
 * 完全无浮点参与，能在代码审查层面彻底排除隐患。
 * 固定 16 次迭代，与输入无关，无数据相关分支。
 */
export function sqrt(x: Fix): Fix {
  if (x <= 0) return 0;
  // 求 isqrt(x * ONE)：结果即为 sqrt(x) 的定点表示
  // 用 32 位逐位法在 uint32 域内完成，先把 x 左移 16 位放进 64 位语义的两段里。
  // 这里改用「先算整数平方根再补精度」的两段式，全程整数。
  let n = x >>> 0;
  // 整数部分平方根（对 x>>16 求 isqrt，得到高位近似）
  let res = 0;
  let bit = 1 << 30;
  while (bit > n) bit >>= 2;
  while (bit !== 0) {
    if (n >= res + bit) {
      n -= res + bit;
      res = (res >> 1) + bit;
    } else {
      res >>= 1;
    }
    bit >>= 2;
  }
  // res = isqrt(x_raw)。而 sqrt_fix(x) = sqrt(x_raw/2^16)*2^16 = isqrt(x_raw)*2^8
  let out = res << 8;
  // 一次牛顿迭代修正低位误差：out = (out + x/out) / 2
  if (out > 0) out = (out + div(x, out)) >> 1;
  return out | 0;
}

/** 向量长度 */
export const length = (x: Fix, y: Fix): Fix => sqrt(mul(x, x) + mul(y, y));

/**
 * 平方距离。用于距离比较时优先使用本函数，避免 sqrt 开销。
 * 注意：结果本身也是定点数，比较时两侧都必须是平方值。
 */
export const distSq = (ax: Fix, ay: Fix, bx: Fix, by: Fix): Fix => {
  const dx = ax - bx;
  const dy = ay - by;
  return mul(dx, dx) + mul(dy, dy);
};

export const dist = (ax: Fix, ay: Fix, bx: Fix, by: Fix): Fix => sqrt(distSq(ax, ay, bx, by));

/** 归一化结果写入的暂存槽，避免热路径分配对象 */
export const normOut = { x: 0 as Fix, y: 0 as Fix };

/** 把向量归一化到单位长度，结果写入 normOut。返回原长度。 */
export function normalize(x: Fix, y: Fix): Fix {
  const len = length(x, y);
  if (len === 0) {
    normOut.x = 0;
    normOut.y = 0;
    return 0;
  }
  normOut.x = div(x, len);
  normOut.y = div(y, len);
  return len;
}

/** tan(22.5°) 的定点值，8 向量化的分界 */
const TAN_22_5: Fix = 27146;

/**
 * 把方向量化到 8 向（四正 + 四对角），结果写入 normOut（已归一化）。
 * 与烘焙精灵的 8 朝向对齐，避免面朝前却沿斜角平移。
 */
export function snapOctant(x: Fix, y: Fix): void {
  const ax = abs(x);
  const ay = abs(y);
  if (ax === 0 && ay === 0) {
    normOut.x = 0;
    normOut.y = 0;
    return;
  }
  const sx: Fix = x < 0 ? -ONE : x > 0 ? ONE : 0;
  const sy: Fix = y < 0 ? -ONE : y > 0 ? ONE : 0;
  if (ax < mul(ay, TAN_22_5)) {
    normOut.x = 0;
    normOut.y = sy;
    return;
  }
  if (ay < mul(ax, TAN_22_5)) {
    normOut.x = sx;
    normOut.y = 0;
    return;
  }
  normalize(sx, sy);
}

/** 线性插值，t ∈ [0, ONE] */
export const lerp = (a: Fix, b: Fix, t: Fix): Fix => (a + mul(b - a, t)) | 0;
